import { Address } from '@frontend/api';

import { LEAD_TIME_UNITS } from 'constants/all';

export const formatLeadTimeLabel = (
  days,
  { unit = 'business_days', precision = 0 },
) => {
  const { unitName } = LEAD_TIME_UNITS[unit];
  if (days == null || isNaN(days) || days === '') {
    return `-- ${unitName}s`;
  } else {
    const spaceForPadding = '\u00a0';
    return `${parseFloat(days).toFixed(precision)} ${
      days === 1 ? `${unitName}${spaceForPadding}` : `${unitName}s`
    }`;
  }
};

const splitIntoChunks = (size, aryIn) => {
  const ary = aryIn.slice();
  const result = [];
  while (ary.length > 0) result.push(ary.splice(0, size));
  return result;
};

const addCommas = (numberString) => {
  const chunks = splitIntoChunks(
    3,
    parseInt(numberString).toString().split('').reverse(),
  );

  const beforeDecimal = chunks
    .map((chunk) => chunk.join(''))
    .join(',')
    .split('')
    .reverse()
    .join('');

  const decimalIndex = numberString.indexOf('.');

  if (decimalIndex === -1) {
    return '' + beforeDecimal;
  } else {
    return '' + beforeDecimal + '.' + numberString.slice(decimalIndex + 1);
  }
};

export const formatFloat = (number, decimalPoints, dropZeroes = false) => {
  if (isNaN(number) || !number) {
    return '';
  }

  if (dropZeroes) {
    return Number(parseFloat(number).toFixed(decimalPoints)).toString();
  }

  return parseFloat(number).toFixed(decimalPoints);
};

export const formatFloatIncludingZero = (number, decimalPoints) => {
  if (isNaN(number)) {
    return '';
  }

  return parseFloat(number).toFixed(decimalPoints);
};

export const formatInt = (number) =>
  number == null || isNaN(number) ? '' : addCommas(parseInt(number).toString());

export const formatPercent = (
  percentIn,
  decimalPlaces = 2,
  disableRounding = false,
) => {
  if (percentIn == null || isNaN(percentIn) || percentIn === '') {
    return '--%';
  }

  let percent = parseFloat(percentIn);
  if (decimalPlaces) {
    if (!disableRounding) {
      //round to decimal places
      percent = percent.toFixed(decimalPlaces);
    } else {
      //truncate at decimal places
      percent = (
        Math.floor(percent * 10 ** decimalPlaces) /
        10 ** decimalPlaces
      ).toFixed(decimalPlaces);
    }
  } else {
    percent = '' + ~~percent;
  }

  return `${percent ? percent : '--'}%`;
};

export const formatMins = (
  minutesIn,
  decimalPlaces = 2,
  disableRounding = false,
) => {
  if (minutesIn == null || isNaN(minutesIn)) {
    return '---';
  }

  let minutes = parseFloat(minutesIn);
  if (decimalPlaces) {
    if (!disableRounding) {
      //round to decimal places
      minutes = minutes.toFixed(decimalPlaces);
    } else {
      //truncate at decimal places
      minutes = (
        Math.floor(minutes * 10 ** decimalPlaces) /
        10 ** decimalPlaces
      ).toFixed(decimalPlaces);
    }
  } else {
    minutes = '' + ~~minutes;
  }

  return `${minutes ? minutes : '--'} min`;
};

export const formatPrice = (
  priceIn,
  decimalPlaces = 2,
  disableRounding = false,
) => {
  if (priceIn == null || isNaN(priceIn)) {
    return '';
  }
  let price = parseFloat(priceIn);

  // custom logic for negative
  let negative = false;
  if (price < 0) {
    negative = true;
    price = price * -1;
  }

  if (decimalPlaces) {
    if (!disableRounding) {
      //round to decimal places
      price = price.toFixed(decimalPlaces);
    } else {
      //truncate at decimal places
      price = (
        Math.floor(price * 10 ** decimalPlaces + 0.00001) /
        10 ** decimalPlaces
      ).toFixed(decimalPlaces);
    }
  } else {
    price = '' + ~~price;
  }

  if (negative) {
    return '$-' + addCommas(price);
  } else {
    return '$' + addCommas(price);
  }
};

export const formatRevisedQuoteNumber = (quoteNumber, revisionNumber) => {
  return `${quoteNumber}${revisionNumber ? `-${revisionNumber}` : ''}`;
};

//strips the protocol (http/https) from a url
export const formatReadableUrl = (url) => url.replace(/(^\w+:|^)\/\//, '');

//if a url does not have a protocol, adds one so that url can be visited
export const formatVisitableUrl = (url) => {
  return url.search(/(^\w+:|^)\/\//) !== 0 ? `https://${url}` : url;
};

/*
  Parses a decimal string
  Has an extra step to strip out redundant periods
  Intended to be used with parsing formatted values on text input
  fields that are formatted like numbers
*/
export const parseDecimalString = (
  value: string | null,
  decimalPlaces?: number | null,
): string | null => {
  let result;

  if (value === null || typeof value === 'undefined') {
    return null;
  }

  if (typeof value === 'string') {
    // Check if the string is in scientific notation (e.g., "1.5e-10", "2E+5")
    const scientificNotationRegex = /^[+-]?\d*\.?\d+[eE][+-]?\d+$/;
    if (scientificNotationRegex.test(value.trim())) {
      result = parseFloat(value);
    } else {
      result = value.replace(/[^\d.-]/g, ''); // valid characters
      if (result === '') {
        return '';
      } else if (result === '.') {
        return '0';
      }

      const periodCount = (result.match(/\./g) || []).length;
      if (periodCount > 1) {
        const firstIndex = result.indexOf('.');
        const strippedString = result.replace(/\./g, '');
        result =
          strippedString.slice(0, firstIndex) +
          '.' +
          strippedString.slice(firstIndex);
      }

      result = parseFloat(result);
    }
  } else {
    result = value;
  }

  if (decimalPlaces && result) {
    //truncate at decimal places
    result = (
      Math.floor(result * 10 ** decimalPlaces + 0.00001) /
      10 ** decimalPlaces
    ).toFixed(decimalPlaces);
  }

  if (result === null || typeof result === 'undefined') {
    return result;
  }
  return result.toString();
};

export const truncateText = (text, maxLength = 60) => {
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
};

type FormatAddressStringProps = {
  address1?: string;
  address2?: string;
  city?: string;
  state?: {
    abbr: string;
  };
  postalCode?: string;
  country?: {
    abbr: string;
  };
};

export const formatAddressString = (address: FormatAddressStringProps) => {
  const { address1, address2, city, state, postalCode, country } = address;
  return `${address1 ? `${address1}, ` : ''}${address2 ? `${address2}, ` : ''}${
    city ?? ''
  }${state ? `, ${state.abbr}` : ''}${postalCode ? ` ${postalCode}, ` : ''}${
    country?.abbr || '--'
  }`;
};

/**
 * returns an array of strings representing each line in a formatted multi-line address
 */
export const formatAddressMultiline = (address: Address) => {
  const { address1, address2, city, state, postalCode, country } = address;
  const cityStatePostal = `${city}${state && `, ${state.abbr}`}${
    postalCode && ` ${postalCode}`
  }`;
  return [
    address1,
    ...(address2 ? [address2] : []),
    cityStatePostal,
    country.abbr,
  ];
};

/**
 * Formats an array of numbers by grouping consecutive sequences of 3 or more into ranges.
 * Examples:
 *   [1, 2, 3, 4] => "1-4"
 *   [1, 2, 7, 8] => "1, 2, 7, 8"
 *   [1, 2, 3, 7, 8, 12, 13, 14, 15] => "1-3, 7, 8, 12-15"
 */
export const formatNumberRanges = (numbers: number[]): string => {
  if (numbers.length === 0) return 'None';

  const sorted = numbers.toSorted((a, b) => a - b);
  const ranges: string[] = [];
  let start = sorted[0];
  let end = sorted[0];

  for (let i = 1; i <= sorted.length; i++) {
    if (i < sorted.length && sorted[i] === end + 1) {
      // Continue the sequence
      end = sorted[i];
    } else {
      // End of sequence, decide how to format it
      const length = end - start + 1;
      if (length >= 3) {
        // Group as range for 3+ consecutive numbers
        ranges.push(`${start}-${end}`);
      } else if (length === 2) {
        // Keep as two separate numbers
        ranges.push(String(start), String(end));
      } else {
        // Single number
        ranges.push(String(start));
      }
      // Start new sequence
      if (i < sorted.length) {
        start = sorted[i];
        end = sorted[i];
      }
    }
  }

  return ranges.join(', ');
};
